import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { useOpenIntakeRecords } from "../lib/api";
import { publicUrl } from "../lib/publicUrl";
import { Loader2, ChevronDown, ChevronUp, Copy } from "lucide-react";

// URL-only page — deliberately not linked from the main nav (see Layout.jsx). Lists open
// (unconsumed, unexpired) intake records by name only; "View" reveals the QR for that one
// record on demand, rather than showing every customer's phone/vehicle details up front.
function QRRow({ id, name }) {
  const [open, setOpen] = useState(false);
  const url = publicUrl(`/create-group?token=${id}`);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied.");
    } catch {
      toast.error("Couldn't copy — copy it manually.");
    }
  };

  return (
    <div
      data-testid={`intake-row-${id}`}
      className="p-4"
    >
      <div className="flex items-center justify-between">
        <span>{name}</span>

        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid={`intake-view-btn-${id}`}
          className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
        >
          {open ? "Hide" : "View"}
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {open && (
        <div
          className="mt-4 rounded-lg border border-slate-200 p-4 bg-white"
          data-testid={`intake-qr-${id}`}
        >
          <QRCodeSVG value={url} size={200} />

          <button
            type="button"
            onClick={copyLink}
            className="mt-3 flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            <Copy size={14} />
            Copy
          </button>
        </div>
      )}
    </div>
  );
}

export default function IntakeQR() {
  const { data, isLoading, isError } = useOpenIntakeRecords();
  const rows = data?.rows || [];

  return (
    <div className="max-w-xl space-y-6" data-testid="intake-qr-page">
      <div>
        <h1 className="font-work text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Intake QR Codes</h1>
        <p className="text-sm text-slate-500 mt-1">Open intake records — click View to get that customer's QR code.</p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 p-4">
            <Loader2 className="animate-spin" size={16} /> Loading…
          </div>
        ) : isError ? (
          <p className="text-sm text-red-600 p-4">Failed to load intake records.</p>
        ) : !rows.length ? (
          <p className="text-sm text-slate-400 p-4" data-testid="intake-qr-empty">
            No open intake records right now.
          </p>
        ) : (
          rows.map((r) => <QRRow key={r.id} id={r.id} name={r.customer_name} />)
        )}
      </div>
    </div>
  );
}
